/**
 * useNodeDeleteInteraction hook
 *
 * Listens for Delete/Backspace key presses when node editing mode is active
 * and a node is selected. Removes the selected vertex from the geometry if
 * the removal would not create an invalid geometry.
 *
 * **Validates: Requirement 16.3** – WHEN a Registered_User selects a node and
 * presses delete, THE Geometry_Editor SHALL remove the node.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  selectIsEditingGeometry,
  selectEditingFeatureId,
  selectEditingLayerId,
  selectSelectedNodeIndex,
  clearSelectedNode,
} from '../store/slices/geometryEditingSlice';
import { updateFeatureGeometryAsync } from '../store/slices/layersSlice';
import { removeNode, isValidAfterRemoval, type Geometry } from '../services/GeometryEditor';
import { validateGeometry, type ValidatableGeometry } from '../utils/validateGeometry';

/**
 * Callback invoked when a node removal is rejected because it would
 * create an invalid geometry.
 */
export type InvalidRemovalCallback = (message: string) => void;

/**
 * Callback invoked when geometry validation detects topology issues
 * after a successful node removal.
 */
export type ValidationWarningCallback = (errors: string[]) => void;

/**
 * Options for the useNodeDeleteInteraction hook.
 */
export interface UseNodeDeleteInteractionOptions {
  /**
   * Optional callback invoked when a node cannot be removed because
   * it would create an invalid geometry. Can be used to show a snackbar
   * or other notification.
   */
  onInvalidRemoval?: InvalidRemovalCallback;
  /**
   * Optional callback invoked when geometry validation fails after a
   * successful node removal. The edit is still committed but the user
   * is warned about topology issues.
   */
  onValidationWarning?: ValidationWarningCallback;
}

/**
 * useNodeDeleteInteraction
 *
 * A React hook that handles removing vertices from a geometry when the user
 * presses the Delete or Backspace key while a node is selected during node
 * editing mode.
 *
 * Behavior:
 * - Only active when `isEditingGeometry` is true AND `selectedNodeIndex` is not null
 * - Listens for 'keydown' events on the document for Delete and Backspace keys
 * - Calls `isValidAfterRemoval()` to check if removal is safe
 * - If safe, calls `removeNode()` from the GeometryEditor service
 * - Dispatches the updated geometry to Redux via `updateFeatureGeometry`
 * - Clears the selected node after successful removal
 * - If removal would create invalid geometry, invokes `onInvalidRemoval` callback
 */
export function useNodeDeleteInteraction({
  onInvalidRemoval,
  onValidationWarning,
}: UseNodeDeleteInteractionOptions = {}): void {
  const dispatch = useAppDispatch();
  const isEditing = useAppSelector(selectIsEditingGeometry);
  const editingFeatureId = useAppSelector(selectEditingFeatureId);
  const editingLayerId = useAppSelector(selectEditingLayerId);
  const selectedNodeIndex = useAppSelector(selectSelectedNodeIndex);
  const layers = useAppSelector((state) => state.layers.layers);

  // Use refs to avoid stale closures in the event handler
  const isEditingRef = useRef(isEditing);
  const editingFeatureIdRef = useRef(editingFeatureId);
  const editingLayerIdRef = useRef(editingLayerId);
  const selectedNodeIndexRef = useRef(selectedNodeIndex);
  const layersRef = useRef(layers);
  const onInvalidRemovalRef = useRef(onInvalidRemoval);
  const onValidationWarningRef = useRef(onValidationWarning);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    editingFeatureIdRef.current = editingFeatureId;
  }, [editingFeatureId]);

  useEffect(() => {
    editingLayerIdRef.current = editingLayerId;
  }, [editingLayerId]);

  useEffect(() => {
    selectedNodeIndexRef.current = selectedNodeIndex;
  }, [selectedNodeIndex]);

  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    onInvalidRemovalRef.current = onInvalidRemoval;
  }, [onInvalidRemoval]);

  useEffect(() => {
    onValidationWarningRef.current = onValidationWarning;
  }, [onValidationWarning]);

  /**
   * Retrieves the geometry of the feature currently being edited.
   */
  const getEditingGeometry = useCallback((): Geometry | null => {
    const featureId = editingFeatureIdRef.current;
    const layerId = editingLayerIdRef.current;
    if (!featureId || !layerId) return null;

    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer || !layer.features) return null;

    const feature = layer.features.find((f) => f.id === featureId);
    if (!feature || !feature.geometry) return null;

    return feature.geometry as unknown as Geometry;
  }, []);

  /**
   * Handles keydown events for node deletion.
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only respond to Delete and Backspace keys
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;

      // Only active when editing mode is on and a node is selected
      if (!isEditingRef.current) return;
      if (selectedNodeIndexRef.current === null) return;

      const featureId = editingFeatureIdRef.current;
      const layerId = editingLayerIdRef.current;
      if (!featureId || !layerId) return;

      const geometry = getEditingGeometry();
      if (!geometry) return;

      const nodeIndex = selectedNodeIndexRef.current;

      // Prevent default browser behavior (e.g., navigating back on Backspace)
      event.preventDefault();

      // Check if removal would create an invalid geometry
      if (!isValidAfterRemoval(geometry, nodeIndex)) {
        if (onInvalidRemovalRef.current) {
          onInvalidRemovalRef.current(
            'Cannot remove this node: removal would create an invalid geometry'
          );
        }
        return;
      }

      // Remove the node
      const result = removeNode(geometry, nodeIndex);

      if (result.success && result.geometry) {
        // Validate the resulting geometry topology
        const validation = validateGeometry(result.geometry as unknown as ValidatableGeometry);
        if (!validation.valid && onValidationWarningRef.current) {
          onValidationWarningRef.current(validation.errors);
        }

        // Optimistically update Redux and persist; the thunk rolls back on failure.
        dispatch(
          updateFeatureGeometryAsync({
            layerId,
            featureId,
            geometry: result.geometry as unknown as GeoJSON.Geometry,
            previousGeometry: JSON.parse(JSON.stringify(geometry)) as GeoJSON.Geometry,
          })
        );

        // Clear the selected node after removal
        dispatch(clearSelectedNode());
      }
    },
    [dispatch, getEditingGeometry]
  );

  /**
   * Register/unregister the keydown event listener on the document.
   * Only active when editing mode is on and a node is selected.
   */
  useEffect(() => {
    if (!isEditing || selectedNodeIndex === null) return;

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, selectedNodeIndex, handleKeyDown]);
}

export default useNodeDeleteInteraction;
